import { useCallback, useEffect, useState } from 'react';
import { CandidatesService } from '../../candidate/services/candidates.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { FeatureJobsService } from '../../dashboard/services/featurejobs.service';
import { Company } from '../models/company-profile.model';
import { ClientsService } from '../services/clients.service';
import { CompanyService } from '../services/company-profile.service';
import { UsersService } from '../services/users.service';
import { CompanyMetrics, emptyCompanyMetrics } from './company-workspace.utils';

type WorkspaceState = 'loading' | 'ready' | 'error';

const logoStorageKey = (companyId: string) => `companyLogo_${companyId}`;

const readStoredLogo = (companyId: string): string =>
  localStorage.getItem(logoStorageKey(companyId)) ?? '';

const storeLogo = (companyId: string, dataUrl: string) => {
  try {
    localStorage.setItem(logoStorageKey(companyId), dataUrl);
  } catch {
    // A private browsing quota should not prevent the logo from being shown.
  }
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const settledCount = (
  result: PromiseSettledResult<{ totalElements?: number } | undefined | null>
): number | null => {
  if (result.status !== 'fulfilled' || result.value == null) {
    return null;
  }
  return result.value.totalElements ?? 0;
};

export const useCompanyWorkspace = () => {
  const { companyId } = useProfile();
  const [company, setCompany] = useState<Company>(new Company());
  const [logo, setLogoState] = useState('');
  const [metrics, setMetrics] = useState<CompanyMetrics>(emptyCompanyMetrics);
  const [workspaceState, setWorkspaceState] =
    useState<WorkspaceState>('loading');
  const [hasPartialMetricError, setHasPartialMetricError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const setLogo = useCallback(
    (dataUrl: string) => {
      setLogoState(dataUrl);
      if (!companyId) return;

      if (dataUrl) {
        storeLogo(companyId, dataUrl);
      } else {
        localStorage.removeItem(logoStorageKey(companyId));
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    const loadWorkspace = async () => {
      setWorkspaceState('loading');
      const companyResult = await Promise.allSettled([
        CompanyService.Get(companyId),
        ClientsService.GetClients(companyId, {
          page: 1,
          size: 1,
          filter: 'deleted:eq:false',
        }),
        UsersService.GetUsers(companyId, {
          page: 1,
          size: 1,
          filter: 'deleted:eq:false',
        }),
        CandidatesService.GetCandidates(companyId, {
          page: 1,
          size: 1,
          filter: 'deleted:eq:false',
        }),
        FeatureJobsService.Get({
          page: 1,
          size: 1,
          filter: 'deleted:eq:false,closed:eq:false',
        }),
      ]);

      if (cancelled) return;
      if (companyResult[0].status === 'rejected') {
        setWorkspaceState('error');
        return;
      }

      setCompany(companyResult[0].value);
      const nextMetrics = {
        clients: settledCount(companyResult[1]),
        users: settledCount(companyResult[2]),
        candidates: settledCount(companyResult[3]),
        activeJobs: settledCount(companyResult[4]),
      };
      setMetrics(nextMetrics);
      setHasPartialMetricError(
        Object.values(nextMetrics).some((value) => value === null)
      );
      setWorkspaceState('ready');
    };

    void loadWorkspace();
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  useEffect(() => {
    if (!companyId || !company.companyFile?.id) {
      setLogoState('');
      return;
    }

    const cachedLogo = readStoredLogo(companyId);
    if (cachedLogo) {
      setLogoState(cachedLogo);
      return;
    }

    let cancelled = false;
    const loadLogo = async () => {
      try {
        const dataUrl = await blobToDataUrl(
          await CompanyService.GetFile(companyId)
        );
        if (!cancelled) setLogo(dataUrl);
      } catch {
        if (!cancelled) setLogoState('');
      }
    };

    void loadLogo();
    return () => {
      cancelled = true;
    };
  }, [company.companyFile?.id, companyId, setLogo]);

  return {
    company,
    companyId,
    hasPartialMetricError,
    logo,
    metrics,
    setCompany,
    setLogo,
    workspaceState,
    reload: () => setRefreshKey((current) => current + 1),
  };
};
